import { createSlice } from "@reduxjs/toolkit";
import {
    isValidLotQuantity,
    resolveCartItemLotSize,
    snapToValidLotQuantity,
    stepLotQuantity,
} from "../utils/lotSize";

const cartSlice = createSlice({
    name: "cart",
    initialState: {
        products: [],
        total: 0,

    },
    reducers: {
        addProduct: (state, action) => {
            const { 
                _id, 
                productID, 
                productName, 
                 unitPrice, 
                qty, 
                image, 
                productWeight, 
                sellerID,
                packageDimensionsID, 
                useManualPackage = false,
                packageLength,
                packageBreadth,
                packageHeight,
                isPreBook = false, 
                MRP,
                deliveryCharge = { isEnabled: false, amount: 0 },
                codRestriction = { isRestricted: false, reason: "" },
                sizeVariant = null,
                lotSize = null,
                productRatePilot = null,
                lineWeight = null,
            } = action.payload;

            const cartKey = sizeVariant 
            ? `${_id}_${sizeVariant.value}` 
            : _id;

            const existingProduct = state.products.find(
                (product) => {
                    const productCartKey = product.sizeVariant 
                        ? `${product._id}_${product.sizeVariant.value}` 
                        : product._id;
                    return productCartKey === cartKey;
                }
            );

            const itemLotSize = lotSize?.baseQuantity && lotSize?.quantityMultiple
                ? { baseQuantity: lotSize.baseQuantity, quantityMultiple: lotSize.quantityMultiple }
                : null;
            const addQty = itemLotSize ? snapToValidLotQuantity(qty, itemLotSize) : qty;

            if (existingProduct) {
                const mergedLot = resolveCartItemLotSize(existingProduct) || itemLotSize;
                let newQty = existingProduct.qty + addQty;
                if (mergedLot) {
                    // Repeat add from product card: step by quantityMultiple, not base again
                    newQty = stepLotQuantity(existingProduct.qty, mergedLot, 1);
                    if (!existingProduct.lotSize && itemLotSize) {
                        existingProduct.lotSize = itemLotSize;
                    }
                }
                const priceDiff = existingProduct.unitPrice * (newQty - existingProduct.qty);
                existingProduct.qty = newQty;
                if (productRatePilot) {
                    existingProduct.productRatePilot = productRatePilot;
                }
                if (lineWeight != null) {
                    existingProduct.lineWeight = Number(lineWeight) || 0;
                }
                if (productWeight != null) {
                    existingProduct.productWeight = productWeight;
                }
                state.total += priceDiff;
            } else {
                const lineLot = itemLotSize || null;
                const lineQty = lineLot ? snapToValidLotQuantity(addQty, lineLot) : addQty;
                state.products.push({ 
                    _id, 
                    productID, 
                    productName, 
                    unitPrice, 
                    qty: lineQty, 
                    image, 
                    productWeight, 
                    sellerID, 
                    packageDimensionsID, 
                    useManualPackage: useManualPackage === true,
                    packageLength,
                    packageBreadth,
                    packageHeight,
                    isPreBook, 
                    MRP,
                    deliveryCharge,
                    codRestriction,
                    sizeVariant,
                    ...(lineLot ? { lotSize: lineLot } : {}),
                    ...(productRatePilot ? { productRatePilot } : {}),
                    ...(lineWeight != null ? { lineWeight: Number(lineWeight) || 0 } : {}),
                });
                state.total += unitPrice * lineQty;
            }
        },
        attachLotSizeToCartItem: (state, action) => {
            const { _id, variantValue, lotSize } = action.payload;
            const product = state.products.find((p) => {
                if (variantValue) {
                    return p._id === _id && p.sizeVariant?.value === variantValue;
                }
                return p._id === _id && !p.sizeVariant;
            });
            if (product && lotSize?.baseQuantity) {
                product.lotSize = lotSize;
            }
        },
        removeFromCart: (state, action) => {

            const { _id, variantValue } = action.payload; 

            const productToRemove = state.products.find(
                (product) => {
                    if (variantValue) {
                        return product._id === _id && 
                               product.sizeVariant?.value === variantValue;
                    }
                    return product._id === _id && !product.sizeVariant;
                }
            );

            if (productToRemove) {
                const totalPrice = productToRemove.qty * productToRemove.unitPrice;
                state.total -= totalPrice;
                state.products = state.products.filter(
                    (product) => {
                        if (variantValue) {
                            return !(product._id === _id && 
                                   product.sizeVariant?.value === variantValue);
                        }
                        return !(product._id === _id && !product.sizeVariant);
                    }
                );
            }
        },

        changeQuantity: (state, action) => {
            const { _id, newQty, variantValue } = action.payload;

            const productToUpdate = state.products.find(
                (product) => {
                    if (variantValue) {
                        return product._id === _id && 
                               product.sizeVariant?.value === variantValue;
                    }
                    return product._id === _id && !product.sizeVariant;
                }
            );

            if (productToUpdate) {
                const lotSize = resolveCartItemLotSize(productToUpdate);
                let finalQty = newQty;
                if (lotSize) {
                    finalQty = snapToValidLotQuantity(newQty, lotSize);
                    if (!isValidLotQuantity(finalQty, lotSize)) {
                        return;
                    }
                } else {
                    finalQty = Math.max(1, Math.floor(Number(newQty)) || 1);
                }
                const oldQty = productToUpdate.qty;
                const pricePerUnit = productToUpdate.unitPrice;
                const priceDiff = pricePerUnit * (finalQty - oldQty);
                productToUpdate.qty = finalQty;
                state.total += priceDiff;
            }
        },
        clearCart: (state) => {
            state.products = [];
            state.total = 0;
        },
        syncCartLinePrices: (state, action) => {
            const updates = Array.isArray(action.payload) ? action.payload : [];
            let totalDelta = 0;

            for (const u of updates) {
                const product = state.products.find((p) => {
                    if (u.variantValue) {
                        return p._id === u._id && p.sizeVariant?.value === u.variantValue;
                    }
                    return p._id === u._id && !p.sizeVariant;
                });
                if (!product || u.unitPrice == null) continue;

                const oldLineTotal = product.unitPrice * product.qty;
                product.unitPrice = Number(u.unitPrice);
                if (u.MRP != null) product.MRP = Number(u.MRP);
                const newLineTotal = product.unitPrice * product.qty;
                totalDelta += newLineTotal - oldLineTotal;
            }

            state.total += totalDelta;
        },


    },
});

export const { addProduct, removeFromCart, changeQuantity, clearCart, attachLotSizeToCartItem, syncCartLinePrices } = cartSlice.actions;
export default cartSlice.reducer;
